import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal } from '@/components';
import { ModalProps } from '@mui/base/Modal';
import clsx from 'clsx';
import { useGetHarborsQuery } from '@/services/HarborManagementService';
import { HarborResponse } from '@/types/harbor-management.types';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import * as Yup from 'yup';
import {
  IPierData,
  IPierFormData,
  CapacityMode,
  PierContentLanguage,
  PierContentTranslations,
  rawImageCaptionsToDto,
} from '@/types/pier-management.types';
import NumberInput from '@/components/inputControls/NumberInput';
import { PierImageInput } from './PierImageInput';


interface HarborOption {
  id: string;
  name: string;
}

const PIER_CONTENT_LANGUAGES: Array<{
  code: PierContentLanguage;
  shortLabel: string;
  label: string;
}> = [
  { code: 'fi', shortLabel: 'FI', label: 'Finnish' },
  { code: 'sv', shortLabel: 'SV', label: 'Swedish' },
  { code: 'en', shortLabel: 'EN', label: 'English' },
  { code: 'de', shortLabel: 'DE', label: 'German' },
];

const emptyTranslations = (): Record<PierContentLanguage, string> => ({
  fi: '',
  sv: '',
  en: '',
  de: '',
});

const normalizeTranslations = (
  translations?: PierContentTranslations | null
): Record<PierContentLanguage, string> => ({
  ...emptyTranslations(),
  ...Object.fromEntries(
    Object.entries(translations || {}).map(([language, value]) => [
      language,
      value || '',
    ])
  ),
});

interface PierModalProps extends Omit<ModalProps, 'children'> {
  mode: 'view' | 'edit' | 'create';
  formData: IPierFormData;
  setFormData: React.Dispatch<React.SetStateAction<IPierFormData>>;
  errors: { [key: string]: string };
  setErrors: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
  onSubmit: () => Promise<void>;
  isSubmitting: boolean;
  pierData?: IPierData | null;
  refetch?: () => void;
}

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required('Pier Name is required')
    .min(2, 'Pier Name must be at least 2 characters')
    .max(50, 'Pier Name must be at most 50 characters'),
  harbor_id: Yup.string().required('Harbor is required'),
  boater_label: Yup.string()
    .max(150, 'Pier description must be at most 150 characters')
    .nullable()
    .optional(),
  approach_note: Yup.string()
    .max(300, 'Approach instructions must be at most 300 characters')
    .nullable()
    .optional(),
  boater_label_translations: Yup.object()
    .shape({
      fi: Yup.string().max(500, 'Finnish pier description must be at most 500 characters').optional(),
      sv: Yup.string().max(500, 'Swedish pier description must be at most 500 characters').optional(),
      en: Yup.string().max(500, 'English pier description must be at most 500 characters').optional(),
      de: Yup.string().max(500, 'German pier description must be at most 500 characters').optional(),
    })
    .nullable()
    .optional(),
  approach_note_translations: Yup.object()
    .shape({
      fi: Yup.string().max(500, 'Finnish approach instructions must be at most 500 characters').optional(),
      sv: Yup.string().max(500, 'Swedish approach instructions must be at most 500 characters').optional(),
      en: Yup.string().max(500, 'English approach instructions must be at most 500 characters').optional(),
      de: Yup.string().max(500, 'German approach instructions must be at most 500 characters').optional(),
    })
    .nullable()
    .optional(),
  capacity_mode: Yup.string()
    .oneOf(
      ['ENUMERATED', 'POOLED', 'SINGLE_BERTH_POOL'],
      'Capacity mode must be enumerated, pooled, or single-berth pool'
    )
    .optional(),
  pooled_capacity: Yup.number()
    .transform((value) => (isNaN(value as number) ? undefined : value))
    .typeError('Pooled capacity must be a number')
    .integer('Pooled capacity must be an integer')
    .min(1, 'Pooled capacity must be at least 1')
    .when('capacity_mode', {
      is: (val: unknown) => val === 'POOLED' || val === 'SINGLE_BERTH_POOL',
      then: (schema) => schema.required('Pooled capacity is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
});

const PierModal = ({
  open,
  onClose,
  mode,
  formData,
  setFormData,
  errors,
  setErrors,
  onSubmit,
  isSubmitting,
  className,
  pierData,
}: PierModalProps) => {
  const [harborOptions, setHarborOptions] = useState<HarborOption[]>([]);
  const [isHarborDropdownOpen, setIsHarborDropdownOpen] = useState(false);
  const [activeContentLanguage, setActiveContentLanguage] =
    useState<PierContentLanguage>('fi');
  const [searchTerm, setSearchTerm] = useState('');
  const selectedHarborId = useSelector(
    (state: RootState) => state.auth.selectedHarborId
  );
  const isHarborDetailsRoute = location.pathname === '/harbors/details';
  const harborDropdownRef = useRef<HTMLDivElement>(null);
  const { data: harborsData, isLoading: isHarborsLoading } = useGetHarborsQuery(
    {}
  );

  const validateField = useCallback(
    async (name: keyof IPierFormData | string, value: unknown) => {
      try {
        const [parentKey, childKey] = (name as string).split('.');
        const candidate = childKey
          ? {
              ...formData,
              [parentKey]: {
                ...((formData as unknown as Record<string, unknown>)[
                  parentKey
                ] as Record<string, unknown> | undefined),
                [childKey]: value,
              },
            }
          : { ...formData, [name]: value };
        await validationSchema.validateAt(name as string, candidate, {
          abortEarly: true,
        });
        setErrors((prev) => ({ ...prev, [name]: '' }));
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          setErrors((prev) => ({
            ...prev,
            [name]: err.errors[0] || 'Invalid input',
          }));
        }
      }
    },
    [setErrors, formData]
  );

  useEffect(() => {
    if (harborsData?.data && open) {
      const options = harborsData.data.map((harbor: HarborResponse) => ({
        id: harbor.id,
        name: harbor.name,
      }));
      if (JSON.stringify(options) !== JSON.stringify(harborOptions)) {
        setHarborOptions(options);
      }

      if (
        (mode === 'edit' || mode === 'view') &&
        pierData &&
        !formData.harbor
      ) {
        const selectedHarbor = harborsData.data.find(
          (harbor: HarborResponse) => harbor.id === pierData.harbor_id
        );
        if (selectedHarbor) {
          setFormData((prev: IPierFormData) => ({
            ...prev,
            harbor_id: selectedHarbor.id,
            harbor: {
              id: selectedHarbor.id,
              name: selectedHarbor.name,
            },
            name: pierData.name || '',
            boater_label: pierData.boater_label || '',
            approach_note: pierData.approach_note || '',
            boater_label_translations: normalizeTranslations(
              pierData.boater_label_translations
            ),
            approach_note_translations: normalizeTranslations(
              pierData.approach_note_translations
            ),
            capacity_mode: pierData.capacity_mode || undefined,
            pooled_capacity: pierData.pooled_capacity || undefined,
            image: pierData.image ?? undefined,
            image_captions: rawImageCaptionsToDto(pierData.image_captions),
          }));
          validateField('harbor_id', selectedHarbor.id);
          validateField('name', pierData.name || '');
        }
      }
    }
  }, [
    harborsData,
    open,
    mode,
    pierData,
    setFormData,
    validateField,
    harborOptions,
    formData.harbor,
  ]);

  useEffect(() => {
    if (!open) {
      setSearchTerm('');
      setIsHarborDropdownOpen(false);
      return;
    }

    if (mode === 'create' && isHarborDetailsRoute && selectedHarborId) {
      const selectedHarbor = harborOptions.find(
        (h) => h.id === selectedHarborId
      );
      if (selectedHarbor && formData.harbor_id !== selectedHarborId) {
        setFormData((prev: IPierFormData) => ({
          ...prev,
          harbor_id: selectedHarborId,
          harbor: {
            id: selectedHarborId,
            name: selectedHarbor.name,
          },
        }));
        validateField('harbor_id', selectedHarborId);
      }
    }
  }, [
    open,
    mode,
    selectedHarborId,
    harborOptions,
    isHarborDetailsRoute,
    setFormData,
    validateField,
    formData.harbor_id,
  ]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setFormData((prev: IPierFormData) => ({ ...prev, [name]: value }));
      validateField(name, value);
    },
    [setFormData, validateField]
  );

  const handleHarborSelect = (harbor: HarborOption) => {
    setFormData((prev: IPierFormData) => ({
      ...prev,
      harbor_id: harbor.id,
      harbor: {
        id: harbor.id,
        name: harbor.name,
      },
    }));
    setErrors((prev) => ({ ...prev, harbor_id: '' }));
    setIsHarborDropdownOpen(false);
    setSearchTerm('');
    validateField('harbor_id', harbor.id);
  };

  const handleHarborDropdownToggle = () => {
    if (mode !== 'view' && !isHarborsLoading && !isSubmitting) {
      setIsHarborDropdownOpen(!isHarborDropdownOpen);
    }
  };

  const handleTranslatedContentChange = (
    field: 'boater_label_translations' | 'approach_note_translations',
    language: PierContentLanguage,
    value: string
  ) => {
    setFormData((prev: IPierFormData) => ({
      ...prev,
      [field]: {
        ...emptyTranslations(),
        ...(prev[field] || {}),
        [language]: value,
      },
    }));
    validateField(`${field}.${language}`, value);
  };

  const hasLanguageContent = (language: PierContentLanguage): boolean => {
    const description =
      formData.boater_label_translations?.[language]?.trim() || '';
    const approach =
      formData.approach_note_translations?.[language]?.trim() || '';
    return Boolean(description || approach);
  };

  const contentFieldError = (
    field: 'boater_label_translations' | 'approach_note_translations',
    language: PierContentLanguage
  ) => errors[`${field}.${language}`] || '';

  const handleSubmit = useCallback(async () => {
    try {
      await validationSchema.validate(formData, { abortEarly: false });
      await onSubmit();
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const newErrors: { [key: string]: string } = {};
        error.inner.forEach((err) => {
          if (err.path) {
            newErrors[err.path] = err.message;
          }
        });
        setErrors(newErrors);
      }
      console.error('Form submission error:', error);
    }
  }, [onSubmit, formData, setErrors]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      className={clsx(
        '!flex items-start justify-center overflow-y-auto py-6',
        className
      )}
      aria-labelledby="pier-modal-title"
      aria-describedby="pier-modal-description"
      aria-label={
        mode === 'create'
          ? 'Create pier'
          : mode === 'edit'
            ? 'Edit pier'
            : 'View pier'
      }
      data-testid="pier-modal"
      role="dialog"
    >
      <div className="bg-gray-100 w-full max-w-2xl mx-4 rounded-xl shadow-2xl flex flex-col max-h-[calc(100dvh-3rem)] overflow-hidden">
        <h1
          id="pier-modal-title"
          className="text-2xl font-semibold text-gray-800 px-8 pt-8 mb-6 contrast:text-white"
        >
          {mode === 'create'
            ? 'Create Pier'
            : mode === 'edit'
              ? 'Edit Pier'
              : 'View Pier'}
        </h1>
        <div id="pier-modal-description" className="sr-only">
          {mode === 'create'
            ? 'Form to create a new pier'
            : mode === 'edit'
              ? 'Form to edit pier details'
              : 'View pier details'}
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto px-8 pr-6 pb-6 custom-scrollbar">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700 mb-2 contrast:text-white/100"
              >
                Pier Name
              </label>
              <input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleInputChange}
                disabled={mode === 'view' || isSubmitting}
                className={clsx(
                  'input input-bordered w-full h-11 rounded-lg border-gray-300',
                  errors.name && 'border-red-500 contrast:border-[#FF8B33]'
                )}
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? 'name-error' : undefined}
                placeholder="Enter pier name"
                required
              />
              {errors.name && (
                <p
                  id="name-error"
                  className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                  role="alert"
                >
                  {errors.name}
                </p>
              )}
            </div>
            <div className="col-span-1">
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 contrast:text-white/70 mb-2">
                Harbor
              </span>
              <div className="relative" ref={harborDropdownRef}>
                <div
                  className={clsx(
                    'input input-bordered w-full h-11 flex items-center justify-between',
                    {
                      'cursor-pointer':
                        !isSubmitting &&
                        mode !== 'view' &&
                        !isHarborDetailsRoute,
                      'cursor-not-allowed bg-gray-100 opacity-50':
                        isSubmitting || mode === 'view' || isHarborDetailsRoute,
                      'border-primary':
                        isHarborDropdownOpen &&
                        mode !== 'view' &&
                        !isHarborDetailsRoute,
                      'border-red-500': errors.harbor_id,
                    }
                  )}
                  onClick={
                    isSubmitting || mode === 'view' || isHarborDetailsRoute
                      ? undefined
                      : handleHarborDropdownToggle
                  }
                  aria-label="Select harbor"
                  aria-invalid={!!errors.harbor_id}
                  aria-describedby={
                    errors.harbor_id ? 'harbor_id-error' : undefined
                  }
                  aria-haspopup="listbox"
                  aria-expanded={isHarborDropdownOpen}
                  role="combobox"
                >
                  <div className="truncate">
                    {formData.harbor?.name ||
                      (isHarborsLoading
                        ? 'Loading harbors...'
                        : 'Select harbor')}
                  </div>
                  <div className="flex items-center">
                    <svg
                      className={clsx(
                        'w-4 h-4 text-gray-400 transition-transform duration-200',
                        isHarborDropdownOpen && 'transform rotate-180'
                      )}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>
                {isHarborDropdownOpen &&
                  !isHarborsLoading &&
                  mode !== 'view' &&
                  !isHarborDetailsRoute && (
                    <div
                      className="absolute z-50 w-full mt-1 bg-gray-100 border border-gray-300 rounded-md shadow-lg"
                      style={{
                        maxHeight: 'calc(100vh - 300px)',
                        overflowY: 'auto',
                      }}
                    >
                      <div className="p-2 border-b">
                        <input
                          type="text"
                          placeholder="Search harbors..."
                          className="w-full p-2 border rounded"
                          value={searchTerm}
                          onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setIsHarborDropdownOpen(true);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      {harborOptions.length > 0 ? (
                        harborOptions.map((harbor) => (
                          <div
                            key={harbor.id}
                            role="option"
                            aria-selected={formData.harbor_id === harbor.id}
                            className={clsx(
                              'px-4 py-2 cursor-pointer hover:bg-gray-100',
                              formData.harbor_id === harbor.id &&
                                'bg-primary-light'
                            )}
                            onClick={() => handleHarborSelect(harbor)}
                          >
                            {harbor.name}
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-2 text-gray-500">
                          {searchTerm && harborOptions.length === 0
                            ? 'Harbor not found'
                            : 'No harbors available'}
                        </div>
                      )}
                    </div>
                  )}
              </div>
              {errors.harbor_id && (
                <p
                  id="harbor_id-error"
                  className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                  role="alert"
                >
                  {errors.harbor_id}
                </p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
            <div className="col-span-1 lg:col-span-2">
              <div className="mb-3">
                <div>
                  <h2 className="text-sm font-semibold text-gray-800">
                    Pier Description & Approach Instructions
                  </h2>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 border-b border-gray-200 px-1">
                {PIER_CONTENT_LANGUAGES.map((language) => (
                  <button
                    key={language.code}
                    type="button"
                    onClick={() => setActiveContentLanguage(language.code)}
                    className={clsx(
                      'px-3 py-2 text-sm font-medium border-b-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1',
                      activeContentLanguage === language.code
                        ? 'border-blue-600 text-blue-700'
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                    )}
                    aria-pressed={activeContentLanguage === language.code}
                  >
                    {language.shortLabel}
                    <span
                      className={clsx(
                        'ml-2 inline-block h-2 w-2 rounded-full',
                        hasLanguageContent(language.code)
                          ? 'bg-emerald-500'
                          : 'bg-gray-300'
                      )}
                    />
                  </button>
                ))}
              </div>
              {PIER_CONTENT_LANGUAGES.map((language) => {
                const isActive = activeContentLanguage === language.code;
                const descriptionError = contentFieldError(
                  'boater_label_translations',
                  language.code
                );
                const approachError = contentFieldError(
                  'approach_note_translations',
                  language.code
                );

                return (
                  <div
                    key={language.code}
                    className={clsx('pt-4 space-y-4', !isActive && 'hidden')}
                  >
                    <div>
                      <label
                        htmlFor={`boater_label_translations_${language.code}`}
                        className="block text-sm font-medium text-gray-700 mb-2 contrast:text-white/100"
                      >
                        Pier description (optional)
                      </label>
                      <textarea
                        id={`boater_label_translations_${language.code}`}
                        name={`boater_label_translations.${language.code}`}
                        value={
                          formData.boater_label_translations?.[
                            language.code
                          ] || ''
                        }
                        onChange={(e) =>
                          handleTranslatedContentChange(
                            'boater_label_translations',
                            language.code,
                            e.target.value
                          )
                        }
                        disabled={mode === 'view' || isSubmitting}
                        className={clsx(
                          'textarea textarea-bordered w-full rounded-lg border-gray-300',
                          descriptionError &&
                            'border-red-500 contrast:border-[#FF8B33]'
                        )}
                        aria-invalid={!!descriptionError}
                        aria-describedby={
                          descriptionError
                            ? `boater_label_translations_${language.code}-error`
                            : undefined
                        }
                        placeholder={`Enter pier description in ${language.label}`}
                        rows={3}
                      />
                      {descriptionError && (
                        <p
                          id={`boater_label_translations_${language.code}-error`}
                          className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                          role="alert"
                        >
                          {descriptionError}
                        </p>
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor={`approach_note_translations_${language.code}`}
                        className="block text-sm font-medium text-gray-700 mb-2 contrast:text-white/100"
                      >
                        Approach instructions (optional)
                      </label>
                      <textarea
                        id={`approach_note_translations_${language.code}`}
                        name={`approach_note_translations.${language.code}`}
                        value={
                          formData.approach_note_translations?.[
                            language.code
                          ] || ''
                        }
                        onChange={(e) =>
                          handleTranslatedContentChange(
                            'approach_note_translations',
                            language.code,
                            e.target.value
                          )
                        }
                        disabled={mode === 'view' || isSubmitting}
                        className={clsx(
                          'textarea textarea-bordered w-full rounded-lg border-gray-300',
                          approachError &&
                            'border-red-500 contrast:border-[#FF8B33]'
                        )}
                        aria-invalid={!!approachError}
                        aria-describedby={
                          approachError
                            ? `approach_note_translations_${language.code}-error`
                            : undefined
                        }
                        placeholder={`Enter approach instructions in ${language.label}`}
                        rows={3}
                      />
                      {approachError && (
                        <p
                          id={`approach_note_translations_${language.code}-error`}
                          className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
                          role="alert"
                        >
                          {approachError}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="col-span-1">
              <label
                htmlFor="capacity_mode"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Capacity Mode
              </label>
              <select
                id="capacity_mode"
                name="capacity_mode"
                value={formData.capacity_mode || ''}
                onChange={(e) => {
                  const { name, value } = e.target;
                  const requiresPooledCapacity =
                    (value as CapacityMode) === 'POOLED' ||
                    (value as CapacityMode) === 'SINGLE_BERTH_POOL';
                  setFormData((prev: IPierFormData) => ({
                    ...prev,
                    [name]: (value as CapacityMode) || undefined,
                    pooled_capacity: requiresPooledCapacity
                      ? prev.pooled_capacity
                      : undefined,
                  }));
                  validateField(name, value || undefined);
                }}
                disabled={mode === 'view' || mode === 'edit' || isSubmitting}
                className={clsx(
                  'select select-bordered w-full h-11 rounded-lg border-gray-300',
                  errors.capacity_mode && 'border-red-500'
                )}
                aria-invalid={!!errors.capacity_mode}
                aria-describedby={
                  errors.capacity_mode ? 'capacity_mode-error' : undefined
                }
              >
                <option value="">Select capacity mode</option>
                <option value="ENUMERATED">
                  Enumerated Berths (B-01…B-10)
                </option>
                <option value="POOLED">
                  Pooled (e.g., "10 visitor spots")
                </option>
                <option value="SINGLE_BERTH_POOL">
                  Single-Berth Pool (one shared berth, capacity-counted)
                </option>
              </select>
              {mode === 'edit' && (
                <p className="text-gray-500 text-xs mt-1">
                  Capacity mode can only be set when creating a pier.
                </p>
              )}
              {errors.capacity_mode && (
                <p
                  id="capacity_mode-error"
                  className="text-red-500 text-xs mt-1"
                  role="alert"
                >
                  {errors.capacity_mode}
                </p>
              )}
            </div>
            {(formData.capacity_mode === 'POOLED' ||
              formData.capacity_mode === 'SINGLE_BERTH_POOL') && (
              <div className="col-span-1">
                <label
                  htmlFor="pooled_capacity"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {formData.capacity_mode === 'SINGLE_BERTH_POOL'
                    ? 'Pool Capacity (units)'
                    : 'Pooled Capacity'}
                </label>
                <NumberInput
                  id="pooled_capacity"
                  name="pooled_capacity"
                  min={1}
                  step={1}
                  value={formData.pooled_capacity ?? ''}
                  onChange={(e) => {
                    const { name, value } = e.target;
                    const parsed = value === '' ? undefined : Number(value);
                    setFormData((prev: IPierFormData) => ({
                      ...prev,
                      [name]: parsed as unknown as number | undefined,
                    }));
                    validateField(name, parsed);
                  }}
                  disabled={mode === 'view' || isSubmitting}
                  className={clsx(
                    'input input-bordered w-full h-11 rounded-lg border-gray-300',
                    errors.pooled_capacity && 'border-red-500'
                  )}
                  aria-invalid={!!errors.pooled_capacity}
                  aria-describedby={
                    errors.pooled_capacity ? 'pooled_capacity-error' : undefined
                  }
                  placeholder="Enter pooled capacity"
                />
                {errors.pooled_capacity && (
                  <p
                    id="pooled_capacity-error"
                    className="text-red-500 text-xs mt-1"
                    role="alert"
                  >
                    {errors.pooled_capacity}
                  </p>
                )}
              </div>
            )}
            <div className="col-span-1 lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2 contrast:text-white/100">
                Pier Image (optional)
              </label>
              {mode === 'view' ? (
                formData.image && typeof formData.image === 'string' ? (
                  <img
                    src={formData.image}
                    alt="Pier layout"
                    className="max-w-[280px] h-auto rounded-lg border border-gray-300"
                  />
                ) : (
                  <p className="text-sm text-gray-500 contrast:text-white/70">
                    No image uploaded
                  </p>
                )
              ) : (
                <PierImageInput
                  value={formData.image}
                  captions={formData.image_captions}
                  disabled={isSubmitting}
                  loading={isSubmitting}
                  error={errors.image}
                  onChange={(media, captions) => {
                    setFormData((prev: IPierFormData) => ({
                      ...prev,
                      ...(media instanceof File ? { image: media } : {}),
                      image_captions: captions,
                    }));
                    setErrors((prev) => ({ ...prev, image: '' }));
                  }}
                  onRemove={() => {
                    setFormData((prev: IPierFormData) => ({
                      ...prev,
                      image: null,
                      image_captions: undefined,
                    }));
                  }}
                />
              )}
            </div>
          </div>
        </div>
        {(mode === 'create' || mode === 'edit') && (
          <div className="px-8 pt-4 pb-8 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => onClose?.({}, 'backdropClick')}
              className="btn btn-sm btn-ghost text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-gray-500"
              aria-label="Cancel"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="btn btn-sm btn-primary bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label={mode === 'create' ? 'Create Pier' : 'Save Pier'}
              disabled={isSubmitting || !!errors.name || !!errors.harbor_id}
              data-testid="modal-submit-button"
            >
              {isSubmitting
                ? 'Saving...'
                : mode === 'create'
                  ? 'Create'
                  : 'Save'}
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export { PierModal };
